/** @jsxImportSource @emotion/react */
import { Box, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Icon } from '@app/components/Atoms/Icon'
import { ImageWithFilters } from '@app/components/Atoms/ImageWithFilters'
import { Link, useNavigate } from 'react-router-dom'
import {
  buttonContainer,
  emptySideDrawerContainer,
  emptySideDrawerContent,
  emptyTitle,
} from './EmptySideDrawer.styles'
import { useLiveContext } from '@app/modules/Live/contexts/Live.Context'
import { showAddLayersModalTabAtom } from '@app/risk-migration/atoms/riskMap.atoms'
import { useAtomValue, useSetAtom } from 'jotai'
import WorkspaceSelector from '@app/modules/Risk/Profile/components/WorkspaceSelector'
import { Button } from '@/components/ui/button'
import { useApplyWorkspace } from '@app/modules/Risk/Workspaces/hooks/useApplyWorkspace'
import { useFeatureFlags } from '@app/hooks/useFeatureFlags'
import { FEATURE_FLAGS } from '@app/app-constants'
import { selectedRiskProfileWorkspaceAtom } from '@app/state/riskAtoms'

export const EmptySideDrawer = ({
  text,
  type,
  buttonText,
}: {
  text: string
  type: 'select' | 'element' | 'hazard' | 'information'
  buttonText?: string
}) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const atomWorkspace = useAtomValue(selectedRiskProfileWorkspaceAtom)

  const { applyWorkspace } = useApplyWorkspace()
  const setShowAddLayersModalTab = useSetAtom(showAddLayersModalTabAtom)
  const clientName = localStorage.getItem('client_name')
  const { liveMapSelected } = useLiveContext()
  const { useFeatureFlag } = useFeatureFlags()
  const workspaceV2Enabled = useFeatureFlag(FEATURE_FLAGS.WORKSPACE_V2)

  if (type === 'select') {
    return (
      <>
        <Box css={emptySideDrawerContainer({ theme })}>
          <Box css={emptySideDrawerContent()}>
            <Icon
              colour={theme.palette.primary.main}
              iconName="s3://select-element.png"
              className="smallicon"
            />
            <Box>
              <Typography variant="subtitle1" css={emptyTitle({ theme })}>
                {text}
              </Typography>
              <Box css={buttonContainer}>
                <Button
                  onClick={() => navigate('/app/guide/tutorials/download-export-and-share-results')}
                  title="Learn how to use the Risk Explorer"
                  variant="outline"
                >
                  Tutorial
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </>
    )
  }

  return (
    <>
      <Box css={emptySideDrawerContainer({ theme, live: liveMapSelected })}>
        <Box css={emptySideDrawerContent()}>
          <ImageWithFilters
            src={
              liveMapSelected
                ? '/images/illustrations/emptySideDrawerLive.svg'
                : '/images/illustrations/emptySideDrawer.svg'
            }
            alt={liveMapSelected ? 'Men handling clocks and gears' : 'Woman looking for hazards'}
            color={theme.palette.primary.main}
            hueStrength={0.9}
            lightenStrength={0.4}
            desaturateStrength={0.4}
            backgroundColor={theme.palette.primary.main + '20'}
            customCss={{
              backgroundColor: theme.palette.background.paper,
              paddingBottom: theme.spacing(4),
              opacity: 0.9,
            }}
          />
          <Box>
            <Typography variant="subtitle1" css={emptyTitle({ theme })}>
              {text}
            </Typography>
            <Box css={buttonContainer}>
              <Button onClick={() => setShowAddLayersModalTab(type)}>+ {buttonText}</Button>

              {workspaceV2Enabled ? (
                <div className="flex gap-2">
                  <WorkspaceSelector location="riskMap" />
                  <Button
                    disabled={!atomWorkspace}
                    onClick={() => applyWorkspace(atomWorkspace)}
                    variant="outline"
                  >
                    Load
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() =>
                    navigate(
                      liveMapSelected
                        ? `/app/live/workspaces/saved`
                        : '/app/risk/explorer/workspaces/saved',
                    )
                  }
                  title="Load Workspace"
                  variant="outline"
                >
                  Load Workspace
                </Button>
              )}

              {clientName && (
                <Box mt={4}>
                  <Typography sx={{ display: 'block', textAlign: 'center', fontSize: 16 }}>
                    Explore our{' '}
                    <Link
                      to={'/guide/tutorials/download-export-and-share-results'}
                      css={{ fontWeight: '600', textDecoration: 'none' }}
                    >
                      Tutorials
                    </Link>
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  )
}
